import React, { useState, useRef, useMemo } from "react";
import "./MetaLiftCampaignCell.scss";
import { Form } from "react-bootstrap";
import { MdDelete, MdSearch, MdClose, MdArrowUpward, MdArrowDownward } from "react-icons/md";
import {
  SelectedTestOptions,
  getActiveTestType,
  getTestTypeLabels,
  getUniqueEntities,
  UniqueEntity,
} from "./MetaLiftStudyUtils";
import { formatMoney, formatPacingNumber } from "@blisspointmedia/bpm-types/dist/formatUtils";
import { MetaBudgetInfo, SelectedItem } from "@blisspointmedia/bpm-types/dist/MetaLiftStudy";

interface MetaLiftCampaignCellProps {
  cellId: string;
  cellName: string;
  onCellNameChange: (newName: string) => void;
  onDelete: () => void;
  canDelete: boolean;
  selectedTestOptions: SelectedTestOptions;
  campaignOptions: MetaBudgetInfo[];
  selectedItems: SelectedItem[];
  onToggleItem: (itemId: string, accountId: string, name: string) => void;
  lockedAccountId: string | null;
  allSelectedItemIds: Set<string>;
}

type SortColumn = "name" | "status" | "budget";
type SortDirection = "asc" | "desc";
interface SortConfig {
  column: SortColumn;
  direction: SortDirection;
}

interface SortableHeaderProps {
  label: string;
  column: SortColumn;
  sortConfigs: SortConfig[];
  onSort: (column: SortColumn) => void;
}

const SortableHeader = ({
  label,
  column,
  sortConfigs,
  onSort,
}: SortableHeaderProps): JSX.Element => {
  const index = sortConfigs.findIndex(s => s.column === column);
  const config = index >= 0 ? sortConfigs[index] : null;

  return (
    <div className="sortableHeader" onClick={() => onSort(column)} role="button" tabIndex={0}>
      <span>{label}</span>
      {config && (
        <span className="sortIndicator">
          {config.direction === "asc" ? <MdArrowUpward size={14} /> : <MdArrowDownward size={14} />}
          {sortConfigs.length > 1 && <span className="sortPriority">{index + 1}</span>}
        </span>
      )}
    </div>
  );
};

const getStatusBadgeClass = (status: string): string => {
  const normalizedStatus = status.toLowerCase();
  if (normalizedStatus === "active") {
    return "statusBadge statusBadgeActive";
  }
  if (normalizedStatus === "paused") {
    return "statusBadge statusBadgePaused";
  }
  if (normalizedStatus === "pending") {
    return "statusBadge statusBadgePending";
  }
  if (normalizedStatus === "archived") {
    return "statusBadge statusBadgeArchived";
  }
  return "statusBadge";
};

const formatBudget = (budget: string, budgetType: string): string => {
  if (!budget || budget === "N/A") {
    return "N/A";
  }

  const numValue = parseFloat(budget);
  if (isNaN(numValue)) {
    return budget;
  }
  const formattedBudget = `${formatPacingNumber(numValue / 100, "$")} USD ${budgetType}`;
  return formattedBudget;
};

interface EntityRowProps {
  entity: UniqueEntity;
  isSelected: boolean;
  isDisabled: boolean;
  isSelectedInOtherCell: boolean;
  onToggle: () => void;
  labels: ReturnType<typeof getTestTypeLabels>;
}

const EntityRow = ({
  entity,
  isSelected,
  isDisabled,
  isSelectedInOtherCell,
  onToggle,
  labels,
}: EntityRowProps): JSX.Element => {
  const rowClassName = `entityRow${isSelected ? " entityRowSelected" : ""}${
    isDisabled ? " entityRowDisabled" : ""
  }${isSelectedInOtherCell ? " entityRowSelectedInOtherCell" : ""}`;

  return (
    <div
      className={rowClassName}
      onClick={!isDisabled && !isSelectedInOtherCell ? onToggle : undefined}
      role="button"
      tabIndex={isDisabled || isSelectedInOtherCell ? -1 : 0}
      onKeyDown={e => {
        if ((e.key === "Enter" || e.key === " ") && !isDisabled && !isSelectedInOtherCell) {
          e.preventDefault();
          onToggle();
        }
      }}
    >
      <div className="entityRowLeft">
        <Form.Check
          type="checkbox"
          checked={isSelected}
          disabled={isDisabled || isSelectedInOtherCell}
          onChange={onToggle}
          onClick={e => e.stopPropagation()}
          className="entityCheckbox"
        />
        <div className="entityInfo">
          <div className="entityName">{entity.name}</div>
          {entity.dateRange && <div className="entityDateRange">{entity.dateRange}</div>}
        </div>
      </div>
      <div className="entityRowRight">
        <span className={getStatusBadgeClass(entity.status)}>{entity.status}</span>
        <span className="entityBudget">{formatBudget(entity.budget, entity.budgetType || "")}</span>
      </div>
    </div>
  );
};

interface SelectedCampaignRowProps {
  entity: UniqueEntity;
  onRemove: () => void;
}

const SelectedCampaignRow = ({ entity, onRemove }: SelectedCampaignRowProps): JSX.Element => {
  const compactBudget = `${formatMoney(parseFloat(entity.budget) / 100, 0)} Daily`;

  return (
    <div className="selectedCampaignRow">
      <span className="selectedCampaignName" title={entity.name}>
        {entity.name}
      </span>
      {compactBudget && (
        <>
          <span className="selectedCampaignDivider">|</span>
          <span className="selectedCampaignBudget">{compactBudget}</span>
        </>
      )}
      <button
        type="button"
        className="selectedCampaignRemoveButton"
        onClick={onRemove}
        aria-label={`Remove ${entity.name}`}
      >
        <MdClose size={16} />
      </button>
    </div>
  );
};

const MetaLiftCampaignCell = ({
  cellId,
  cellName,
  onCellNameChange,
  onDelete,
  canDelete,
  selectedTestOptions,
  campaignOptions,
  selectedItems,
  onToggleItem,
  lockedAccountId,
  allSelectedItemIds,
}: MetaLiftCampaignCellProps): JSX.Element => {
  const [searchText, setSearchText] = useState<string>("");
  const [sortConfigs, setSortConfigs] = useState<SortConfig[]>([
    { column: "status", direction: "asc" },
  ]);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleSort = (column: SortColumn) => {
    setSortConfigs(prev => {
      const existingIndex = prev.findIndex(s => s.column === column);

      if (existingIndex === 0) {
        const newConfigs = [...prev];
        newConfigs[0] = {
          ...newConfigs[0],
          direction: newConfigs[0].direction === "asc" ? "desc" : "asc",
        };
        return newConfigs;
      }

      if (existingIndex > 0) {
        const newConfigs = prev.filter(s => s.column !== column);
        return [{ column, direction: "asc" }, ...newConfigs];
      }

      return [{ column, direction: "asc" }, ...prev];
    });
  };

  const testType = getActiveTestType(selectedTestOptions);
  const labels = getTestTypeLabels(testType);

  const allEntities = useMemo(() => {
    return getUniqueEntities(campaignOptions, testType);
  }, [campaignOptions, testType]);

  const filteredEntities = useMemo(() => {
    const filtered = allEntities.filter(entity => {
      const matchesSearch =
        searchText === "" || entity.name.toLowerCase().includes(searchText.toLowerCase());

      return matchesSearch;
    });

    return filtered.sort((a, b) => {
      for (const { column, direction } of sortConfigs) {
        const dir = direction === "asc" ? 1 : -1;
        let result = 0;

        if (column === "name") {
          result = a.name.localeCompare(b.name);
        } else if (column === "status") {
          result = a.status.localeCompare(b.status);
        } else if (column === "budget") {
          result = (parseFloat(a.budget) || 0) - (parseFloat(b.budget) || 0);
        }

        if (result !== 0) {
          return dir * result;
        }
      }
      return 0;
    });
  }, [allEntities, searchText, sortConfigs]);

  const selectedEntitiesData = useMemo(() => {
    const selectedIds = new Set(selectedItems.map(item => item.id));
    return allEntities.filter(entity => selectedIds.has(entity.id));
  }, [allEntities, selectedItems]);

  const totalBudget = useMemo(() => {
    const selectedIds = new Set(selectedItems.map(item => item.id));
    const selectedEntities = allEntities.filter(entity => selectedIds.has(entity.id));

    let total = 0;
    selectedEntities.forEach(entity => {
      if (entity.budget && entity.budget !== "N/A") {
        const numValue = parseFloat(entity.budget);
        if (!isNaN(numValue)) {
          total += numValue / 100;
        }
      }
    });

    if (total === 0) {
      return null;
    }

    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(total);
  }, [allEntities, selectedItems]);

  const isItemSelected = (itemId: string): boolean => {
    return selectedItems.some(item => item.id === itemId);
  };

  const isItemDisabled = (entity: UniqueEntity): boolean => {
    if (lockedAccountId && entity.accountId !== lockedAccountId) {
      return true;
    }
    return false;
  };

  const isItemSelectedInOtherCell = (itemId: string): boolean => {
    const isSelectedGlobally = allSelectedItemIds.has(itemId);
    const isSelectedHere = selectedItems.some(item => item.id === itemId);
    return isSelectedGlobally && !isSelectedHere;
  };

  const handleToggleItem = (entity: UniqueEntity) => {
    if (!isItemDisabled(entity) && !isItemSelectedInOtherCell(entity.id)) {
      onToggleItem(entity.id, entity.accountId, entity.name);
    }
  };

  return (
    <div className="metaLiftCampaignCellOuter">
      <div className="metaLiftCampaignCellInner">
        <div className="metaLiftCampaignCellInnerHeader">
          <input
            type="text"
            className="metaLiftCampaignCellInnerHeaderTitle"
            value={cellName}
            onChange={e => onCellNameChange(e.target.value)}
            placeholder="Campaign Cell Name"
          />
          <MdDelete
            className={
              canDelete
                ? "metaLiftCampaignCellDeleteButton"
                : "metaLiftCampaignCellDeleteButton metaLiftCampaignCellDeleteButtonDisabled"
            }
            style={{ cursor: canDelete ? "pointer" : "not-allowed" }}
            onClick={canDelete ? onDelete : undefined}
            aria-label={
              canDelete ? "Delete campaign cell" : "Cannot delete - at least one cell is required"
            }
            tabIndex={canDelete ? 0 : -1}
            role="button"
            size={25}
          />
        </div>
        <div className="metaLiftCampaignCellInnerHeaderSubTitle">
          A good cell name should be clear, consistent, and descriptive—clearly indicating the
          channel, campaign, test type, audience, time frame, and whether it's a test or control
          group.
        </div>
        <div className="metaLiftCampaignCellInnerBody">
          <div className="metaLiftCampaignCellSection">
            <div className="metaLiftCampaignCellCampaignListTitle">{labels.selectedLabel}</div>
            <div className="metaLiftCampaignCellCampaignListSubtitle">
              {selectedEntitiesData.length === 0 ? (
                labels.noSelectionLabel
              ) : (
                <div className="selectedCampaignsList">
                  {selectedEntitiesData.map(entity => (
                    <SelectedCampaignRow
                      key={entity.id}
                      entity={entity}
                      onRemove={() => handleToggleItem(entity)}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
          <div className="metaLiftCampaignCellSection">
            <div className="metaLiftCampaignCellCampaignListTitle">Cell Budget</div>
            <div className="metaLiftCampaignCellCampaignListSubtitle">
              {totalBudget
                ? `${totalBudget} daily`
                : "Select a timeframe first to get budget amount for this cell."}
            </div>
          </div>
          <div className="metaLiftCampaignCellCampaignsAvailableToTest">
            <div className="metaLiftCampaignCellCampaignsAvailableToTestTitle">
              {labels.availableLabel}
            </div>
            <div
              className="metaLiftCampaignCellSearchInputContainer"
              onClick={() => {
                inputRef.current?.focus();
              }}
            >
              <div className="pageSearchIconContainer" id="pageSearchIconContainer">
                <MdSearch className="pageSearchIcon" />
              </div>
              <Form.Control
                ref={inputRef}
                id="pageSearchInput"
                className="pageSearchInput"
                onChange={e => setSearchText(e.target.value)}
                placeholder={`Search ${labels.plural}`}
                value={searchText}
              />
            </div>
            <div className="metaLiftCampaignsAvailableToTestHeader">
              <div className="headerLeft">
                <SortableHeader
                  label={labels.nameColumn}
                  column="name"
                  sortConfigs={sortConfigs}
                  onSort={handleSort}
                />
              </div>
              <div className="metaLiftRightGroup">
                <div className="metaLifeRightGroupStatus">
                  <SortableHeader
                    label={labels.statusColumn}
                    column="status"
                    sortConfigs={sortConfigs}
                    onSort={handleSort}
                  />
                </div>
                <div className="metaLifeRightGroupBudget">
                  <SortableHeader
                    label="Budget"
                    column="budget"
                    sortConfigs={sortConfigs}
                    onSort={handleSort}
                  />
                </div>
              </div>
            </div>
            <div className="entityListContainer">
              {filteredEntities.length === 0 ? (
                <div className="entityListEmpty">
                  {searchText
                    ? `No ${labels.plural.toLowerCase()} found matching "${searchText}"`
                    : `No ${labels.plural.toLowerCase()} available`}
                </div>
              ) : (
                filteredEntities.map(entity => (
                  <EntityRow
                    key={entity.id}
                    entity={entity}
                    isSelected={isItemSelected(entity.id)}
                    isDisabled={isItemDisabled(entity)}
                    isSelectedInOtherCell={isItemSelectedInOtherCell(entity.id)}
                    onToggle={() => handleToggleItem(entity)}
                    labels={labels}
                  />
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MetaLiftCampaignCell;
